import { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../lib/api";

const RANGES = [
  { label: "24H", days: 1 },
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
];

export function PortfolioChart() {
  const [days, setDays] = useState(7);
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/crypto/portfolio/history?days=${days}`)
      .then(({ data }) => { if (active) setPoints(data.available ? data.points : []); })
      .catch(() => { if (active) setPoints([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  const { change, pct, up } = useMemo(() => {
    if (!points || points.length < 2) return { change: 0, pct: 0, up: true };
    const first = points[0].v, last = points[points.length - 1].v;
    const ch = last - first;
    return { change: ch, pct: first ? (ch / first) * 100 : 0, up: ch >= 0 };
  }, [points]);

  if (!loading && (!points || points.length === 0)) return null;

  const stroke = up ? "#22C55E" : "#EF4343";

  return (
    <div className="rounded-3xl bg-card border border-border p-5 mt-4" data-testid="portfolio-chart">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Portfolio value</div>
          {points && points.length >= 2 && (
            <div className={`flex items-center gap-1 text-sm font-semibold mt-1 ${up ? "text-success" : "text-destructive"}`} data-testid="portfolio-chart-change">
              {up ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
              {up ? "+" : ""}${Math.abs(change).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-muted-foreground font-medium">({up ? "+" : ""}{pct.toFixed(2)}%)</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 bg-secondary rounded-full p-1">
          {RANGES.map((r) => (
            <button key={r.days} data-testid={`chart-range-${r.label}`} onClick={() => setDays(r.days)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${days === r.days ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-36 mt-3 -mx-1">
        {loading ? (
          <div className="h-full w-full grid place-items-center text-xs text-muted-foreground">Loading…</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="pf-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis domain={["dataMin", "dataMax"]} hide />
              <Tooltip
                cursor={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }}
                labelFormatter={(_, p) => (p && p[0] ? new Date(p[0].payload.t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }) : "")}
                formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Value"]} />
              <Area type="monotone" dataKey="v" stroke={stroke} strokeWidth={2.5} fill="url(#pf-fill)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
